import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type RegisteredDataSource } from "@/lib/forex-api";

interface DataSource {
  id: string;
  name: string;
  type: "market_data" | "news" | "economic_calendar" | "broker_feedback" | "internal_history";
  status: "available" | "degraded" | "unavailable";
  coveredSymbols: string;
  latency: string;
  dataQuality: "excellent" | "good" | "fair" | "poor" | null;
  lastSyncTime: string;
  version: string;
  registrationSource: string;
}

function mapDataSource(source: RegisteredDataSource): DataSource {
  const typeMap: Record<string, DataSource["type"]> = {
    market: "market_data",
    macro: "economic_calendar",
    execution: "broker_feedback",
  };
  const statusMap: Record<string, DataSource["status"]> = {
    enabled: "available",
    disabled: "unavailable",
  };
  const latency = source.health?.latency_ms != null ? `${source.health.latency_ms}ms` : "未提供";
  const rate = source.health?.available_rate;
  return {
    id: source.source_id,
    name: source.name,
    type: typeMap[source.capability] || "internal_history",
    status: statusMap[source.status] || "degraded",
    coveredSymbols: source.capability === "market" ? "服务端注册行情品种" : source.capability === "macro" ? "宏观事件与经济日历" : "主账号/Terminal/EA 执行事实",
    latency,
    dataQuality: rate == null ? null : rate >= 99.9 ? "excellent" : rate >= 99 ? "good" : rate >= 95 ? "fair" : "poor",
    lastSyncTime: "采样时间未提供",
    version: source.authority_level,
    registrationSource: source.provider,
  };
}

const typeLabels: Record<string, { label: string; icon: string; cls: string }> = {
  market_data: { label: "行情源", icon: "ri-line-chart-line", cls: "bg-emerald-500/10 text-emerald-500" },
  news: { label: "新闻源", icon: "ri-newspaper-line", cls: "bg-rose-500/10 text-rose-500" },
  economic_calendar: { label: "经济日历源", icon: "ri-calendar-event-line", cls: "bg-amber-500/10 text-amber-500" },
  broker_feedback: { label: "Broker/Terminal回传源", icon: "ri-terminal-box-line", cls: "bg-cyan-500/10 text-cyan-500" },
  internal_history: { label: "内部历史源", icon: "ri-archive-line", cls: "bg-violet-500/10 text-violet-500" },
};

const statusChip: Record<string, { cls: string; label: string }> = {
  available: { cls: "bg-emerald-500/10 text-emerald-500", label: "已注册启用" },
  degraded: { cls: "bg-amber-500/10 text-amber-500", label: "登记状态待核实" },
  unavailable: { cls: "bg-red-500/10 text-red-500", label: "登记停用" },
};

const qualityChip: Record<string, { cls: string; label: string }> = {
  excellent: { cls: "bg-emerald-500/10 text-emerald-500", label: "优秀" },
  good: { cls: "bg-brand/10 text-brand", label: "良好" },
  fair: { cls: "bg-amber-500/10 text-amber-500", label: "一般" },
  poor: { cls: "bg-red-500/10 text-red-500", label: "较差" },
};

export default function DataSourcesPage() {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [remoteReady, setRemoteReady] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    forexApi.listRegisteredDataSources()
      .then((response) => {
        if (!active) return;
        setSources(response.items.map(mapDataSource));
        setRemoteReady(true);
      })
      .catch(() => {
        if (!active) return;
        setSources([]);
        setRemoteReady(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filtered = sources.filter((s) => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !s.id.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter && s.type !== typeFilter) return false;
    if (statusFilter && s.status !== statusFilter) return false;
    return true;
  });

  const stats = {
    total: sources.length,
    available: sources.filter((s) => s.status === "available").length,
    degraded: sources.filter((s) => s.status === "degraded").length,
    unavailable: sources.filter((s) => s.status === "unavailable").length,
    marketData: sources.filter((s) => s.type === "market_data").length,
    news: sources.filter((s) => s.type === "news").length,
    economicCalendar: sources.filter((s) => s.type === "economic_calendar").length,
    brokerFeedback: sources.filter((s) => s.type === "broker_feedback").length,
    internalHistory: sources.filter((s) => s.type === "internal_history").length,
  };

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "数据源目录" }]} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">数据源目录</h2>
          <p className="text-xs text-text-muted mt-0.5">{loading ? "正在读取注册目录..." : remoteReady ? "已读取服务端注册目录；启用状态与实时健康分别核对" : "数据源接口不可用，当前注册状态未知"}</p>
        </div>
      </div>

      <p className="rounded-lg border border-surface-border bg-white p-3 text-xs text-text-muted">注册启用仅表示目录登记状态，不代表此刻连接健康或可用于执行。健康评级和延迟来自接口字段；接口未给采样时间，不能据此判断实时状态。</p>

      {/* Notice */}
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <i className="ri-information-line text-amber-500 text-sm" />
          <span className="text-xs font-semibold text-text-primary">数据源管理说明</span>
        </div>
        <p className="text-[11px] text-text-secondary mt-1.5 leading-relaxed">
          数据源属于系统能力，不属于运营后台配置。B 端后台<strong>不新增、不编辑、不维护 provider credential、不测试连接</strong>。
          如需新增或修改数据源连接参数，请联系技术团队通过代码注册。创建智能体时，从本清单中勾选所需数据源即可。
        </p>
      </div>

      {/* KPI stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">总数</p>
          <p className="text-lg font-semibold font-mono text-text-primary">{remoteReady ? stats.total : "—"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">注册启用</p>
          <p className="text-lg font-semibold font-mono text-emerald-500">{remoteReady ? stats.available : "—"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">降级</p>
          <p className="text-lg font-semibold font-mono text-amber-500">{remoteReady ? stats.degraded : "—"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">登记停用</p>
          <p className="text-lg font-semibold font-mono text-red-500">{remoteReady ? stats.unavailable : "—"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">行情源</p>
          <p className="text-lg font-semibold font-mono text-emerald-500">{remoteReady ? stats.marketData : "—"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">新闻源</p>
          <p className="text-lg font-semibold font-mono text-rose-500">{remoteReady ? stats.news : "—"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">Broker回传</p>
          <p className="text-lg font-semibold font-mono text-cyan-500">{remoteReady ? stats.brokerFeedback : "—"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
          <p className="text-[10px] text-text-muted">内部历史</p>
          <p className="text-lg font-semibold font-mono text-violet-500">{remoteReady ? stats.internalHistory : "—"}</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
          <input type="text" placeholder="搜索数据源..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-[240px] pl-9 pr-3 rounded-lg bg-surface text-sm text-text-primary placeholder:text-text-muted border border-surface-border focus:border-brand/50 focus:outline-none" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
          <option value="">全部类型</option>
          <option value="market_data">行情源</option>
          <option value="news">新闻源</option>
          <option value="economic_calendar">经济日历源</option>
          <option value="broker_feedback">Broker/Terminal回传源</option>
          <option value="internal_history">内部历史源</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
          <option value="">全部状态</option>
          <option value="available">注册启用</option>
          <option value="degraded">降级</option>
          <option value="unavailable">登记停用</option>
        </select>
        <span className="text-xs text-text-muted ml-auto">共 {filtered.length} 条</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">ID</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">名称</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">业务类型</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">注册状态</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">覆盖品种</th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px]">延迟</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">健康采样评级</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">采样时间</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">权威层级</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">查看</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                  <td className="px-4 py-2.5 text-text-primary font-mono text-[11px]">{s.id}</td>
                  <td className="px-4 py-2.5 text-text-primary text-xs font-medium">{s.name}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${typeLabels[s.type].cls}`}>
                      <i className={`${typeLabels[s.type].icon} text-[9px]`} />{typeLabels[s.type].label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusChip[s.status].cls}`}>
                      <i className={`text-[8px] ${s.status === "available" ? "ri-checkbox-circle-fill" : s.status === "degraded" ? "ri-error-warning-fill" : "ri-close-circle-fill"}`} />
                      {statusChip[s.status].label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-text-primary text-[10px] max-w-[220px] truncate" title={s.coveredSymbols}>{s.coveredSymbols}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="font-mono text-xs text-text-primary">{s.latency}</span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {s.dataQuality ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${qualityChip[s.dataQuality].cls}`}>{qualityChip[s.dataQuality].label}</span> : <span className="text-xs text-text-muted">未提供</span>}
                  </td>
                  <td className="px-4 py-2.5 text-text-muted font-mono text-[10px]">{s.lastSyncTime}</td>
                  <td className="px-4 py-2.5 text-text-muted font-mono text-[11px]">{s.version}</td>
                  <td className="px-4 py-2.5 text-center">
                    <Link to={`/forex/data-sources/detail?id=${s.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-eye-line text-xs" />详情
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-lg border border-surface-border p-12 text-center">
          <i className="ri-database-2-line text-3xl text-text-muted" />
          <p className="text-sm text-text-secondary mt-3">没有匹配的数据源</p>
        </div>
      )}
    </div>
  );
}
